import type { ChangeEvent, FC } from 'react'
import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import { push } from 'connected-react-router'
import { SearchForm } from '@/components/composed/forms'
import { getLocationQuery } from '@/logic/app/reducer'
import { loadData } from '@/logic/data/actions'
import { injectMessages } from '@/logic/translation/utils'
import { useFaqGtmEventHandlers } from '@/pages/QuestionsAndAnswers/gtmHelpers'
import { EFaqRouteUrl } from '@/pages/QuestionsAndAnswers/types'
import type { RootState } from '@/redux/interfaces'
import { changeQueryString, removeQueryParameter } from '@/utils/url'
import type { IFaqSearchInputProps } from './interfaces'
import messages from './messages.json'
import './styles.scss'

injectMessages(messages)

const mapState = (state: RootState) => ({
  textInQuery: getLocationQuery(state)?.text,
})

const doNothing = () => {}

const FaqSearchInput: FC<IFaqSearchInputProps> = ({ shouldRedirectToSearchingPage, className }) => {
  const dispatch = useDispatch()
  const { textInQuery } = useSelector(mapState)
  //eslint-disable-next-line react-hooks/exhaustive-deps
  const initialText = useMemo(() => textInQuery as string, [])
  const loadArticles = () => dispatch(loadData({ name: 'faqQuestions', shouldLoadForcibly: true }))
  const { pushChangeEventToGtm, pushFocusEventToGtm } = useFaqGtmEventHandlers()

  const searchArticles = (text: string) => {
    if (!text) {
      removeQueryParameter('text')
      loadArticles()

      return
    }

    if (shouldRedirectToSearchingPage) {
      dispatch(push({ pathname: EFaqRouteUrl.SEARCH, search: `?text=${text}` }))

      return
    }

    changeQueryString('text', text, true)
    removeQueryParameter('tags')
    removeQueryParameter('categoryId')
    changeQueryString('pageNumber', 1, true)
    loadArticles()
  }

  const handleChange = ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    searchArticles(target.value.trim())
    pushChangeEventToGtm()
  }

  return (
    <SearchForm
      searchInputProps={{ iconPosition: 'right' }}
      onSubmit={doNothing}
      fieldName="text"
      fieldId="faqSearchInput"
      className={classNames(className, 'faq-search-input')}
      inputProps={{ defaultValue: initialText, onChange: event => handleChange(event), onFocus: pushFocusEventToGtm }}
    />
  )
}

export default FaqSearchInput
